"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "motion/react";
import type { HOME_GLANCE } from "@/design-data/home-data";
import { Z_CAROUSEL_ARROW } from "@/components/layout/zIndex";

type GlanceItem = (typeof HOME_GLANCE)[number];

// Editorial statistic sizing, tiered by length: 07/03/05/06 stay the
// prominent "punchline" size; 13,032 steps down; 1,046km (longest, widest
// at a given size) steps down further still so nothing overflows the card.
function valueSizeClass(value: string) {
  if (value.length <= 2) return "text-[2.5rem] sm:text-[2.9rem]";
  if (value.length <= 6) return "text-[1.55rem] sm:text-[1.8rem]";
  return "text-[1.25rem] sm:text-[1.45rem]";
}

// 07/03/05/06 sit beside their title (grid, number column shrink-to-fit).
// 13,032 / 1,046km stack instead — beside a long value the title had almost
// no width left to wrap into, which read as cramped and unbalanced.
function isShortValue(value: string) {
  return value.length <= 2;
}

// The existing `gist` field is multi-sentence body copy written for the old
// expandable panel — too long for a compact card. Derives a short, genuine
// excerpt (not new copy) by cutting at a word boundary near maxLen.
function shortDescription(gist: string, maxLen = 108) {
  if (gist.length <= maxLen) return gist;
  return gist.slice(0, maxLen).replace(/\s+\S*$/, "") + "…";
}

// LEGACY FALLBACK. Each card now carries its own `href` and `linkLabel`,
// editable in the admin under "One Country, Every Kind of Landscape (Stat
// Cards)".
//
// This table is the mechanism that replaced: it matched a card to its button
// by the card's exact title text, which made titles quietly load-bearing.
// An admin fixing a comma in "Regions, Each Its Own World" made the lookup
// miss and the button disappear, with no error anywhere.
//
// It stays only so rows saved before those fields existed keep their buttons
// on the day this ships. Once every row carries an href, delete it.
const LEGACY_ROUTE_BY_LABEL: Record<string, { href: string; linkLabel: string }> = {
  "Regions, Each Its Own World": { href: "/#regions", linkLabel: "Explore Regions" },
  "Great Mountain Ranges": { href: "/mountains", linkLabel: "Explore Mountain Ranges" },
  "Glaciers Across Pakistan": { href: "/mountains#glaciers", linkLabel: "View Glaciers" },
  "Of the World's 14 Peaks Over 8,000m": { href: "/mountains#peaks", linkLabel: "Explore High Peaks" },
  "Coastline Along the Arabian Sea": { href: "/experiences/coastal-marine", linkLabel: "Explore the Coast" },
  "UNESCO World Heritage Sites, Plus 29 on the Tentative List": {
    href: "/experiences/heritage",
    linkLabel: "Explore Heritage Sites",
  },
};

/**
 * The card's own link, or the legacy title lookup for rows that predate it.
 *
 * Both parts must be present to render a button: an href with no label gives
 * an unlabelled control, and a label with no href gives a link to nowhere.
 * The values are read defensively because these arrive from the database as
 * free-form JSON, cast to the item type rather than validated against it.
 */
function routeFor(item: GlanceItem): { href: string; linkLabel: string } | null {
  const href = typeof item.href === "string" ? item.href.trim() : "";
  const linkLabel = typeof item.linkLabel === "string" ? item.linkLabel.trim() : "";
  if (href && linkLabel) return { href, linkLabel };
  return LEGACY_ROUTE_BY_LABEL[item.label] ?? null;
}

// This grid's `items` prop is CMS-sourced (HomeSection "glance" row in the
// database — see HomePage.tsx), so editing home-data.ts's image paths alone
// doesn't change what's actually served without a reseed. Same override
// pattern as ROUTE_BY_LABEL: keyed by label, applied on top of whatever
// image the CMS row currently holds, no database write required.
const IMAGE_OVERRIDE_BY_LABEL: Record<string, string> = {
  "Great Mountain Ranges": "/images/karakoram-highway-mountain-ranges.jpg",
};

type StageConfig = {
  cardWidth: number;
  imageHeight: number;
  contentHeight: number;
  spacing: number;
  scaleStep: number;
  opacityStep: number;
  maxVisible: number;
};

// contentHeight fits the editorial headline in its tallest real case: a
// stacked long-value card (number line + up to 2 title lines) at mobile
// width, where the title wraps most aggressively.
const DESKTOP: StageConfig = {
  cardWidth: 320,
  imageHeight: 230,
  contentHeight: 244,
  spacing: 205,
  scaleStep: 0.16,
  opacityStep: 0.32,
  maxVisible: 2,
};
const TABLET: StageConfig = {
  cardWidth: 300,
  imageHeight: 210,
  contentHeight: 250,
  spacing: 210,
  scaleStep: 0.14,
  opacityStep: 0.55,
  maxVisible: 1,
};
const MOBILE: StageConfig = {
  cardWidth: 258,
  imageHeight: 180,
  contentHeight: 250,
  spacing: 195,
  scaleStep: 0.12,
  opacityStep: 0.65,
  maxVisible: 1,
};

function useStageConfig(): StageConfig {
  const [config, setConfig] = useState<StageConfig>(DESKTOP);

  useEffect(() => {
    const mqTablet = window.matchMedia("(max-width: 1023px)");
    const mqMobile = window.matchMedia("(max-width: 639px)");
    function update() {
      if (mqMobile.matches) setConfig(MOBILE);
      else if (mqTablet.matches) setConfig(TABLET);
      else setConfig(DESKTOP);
    }
    update();
    mqTablet.addEventListener("change", update);
    mqMobile.addEventListener("change", update);
    return () => {
      mqTablet.removeEventListener("change", update);
      mqMobile.removeEventListener("change", update);
    };
  }, []);

  return config;
}

// Shortest signed distance from `index` to `activeIndex` around a loop of
// length n — so navigating from the last card to the first (or back) always
// animates the short way round instead of sliding across every card.
function loopedOffset(index: number, activeIndex: number, n: number) {
  let raw = index - activeIndex;
  if (raw > n / 2) raw -= n;
  if (raw < -n / 2) raw += n;
  return raw;
}

const SWIPE_DISTANCE_THRESHOLD = 60;
const SWIPE_VELOCITY_THRESHOLD = 400;

export function HomeGlanceGrid({ items }: { items: GlanceItem[] }) {
  const n = items.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const config = useStageConfig();
  const reduceMotion = useReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);

  function goTo(index: number) {
    setActiveIndex(((index % n) + n) % n);
  }
  function next() {
    goTo(activeIndex + 1);
  }
  function prev() {
    goTo(activeIndex - 1);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    }
  }

  // Native pointer events instead of a Motion `drag` overlay: an overlay
  // element positioned above the cards to catch swipes also intercepts
  // clicks meant for the side cards underneath. Listening on the stage
  // itself lets pointer events bubble normally from each card, so
  // click-to-center keeps working, while still detecting horizontal swipes.
  const pointerStart = useRef<{ x: number; t: number } | null>(null);

  function onPointerDown(e: React.PointerEvent) {
    pointerStart.current = { x: e.clientX, t: performance.now() };
  }
  function onPointerUp(e: React.PointerEvent) {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dt = Math.max(1, performance.now() - start.t);
    const velocity = (dx / dt) * 1000;
    if (dx <= -SWIPE_DISTANCE_THRESHOLD || velocity <= -SWIPE_VELOCITY_THRESHOLD) next();
    else if (dx >= SWIPE_DISTANCE_THRESHOLD || velocity >= SWIPE_VELOCITY_THRESHOLD) prev();
  }

  const stageHeight = config.imageHeight + config.contentHeight;
  const transition = reduceMotion ? { duration: 0 } : { type: "spring" as const, stiffness: 300, damping: 32 };

  return (
    <div className="mt-10">
      <div
        ref={stageRef}
        role="group"
        aria-roledescription="carousel"
        aria-label="Pakistan at a glance"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        className="isolate relative w-full touch-pan-y overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
        style={{ height: stageHeight }}
      >
        {items.map((g, index) => {
          const offset = loopedOffset(index, activeIndex, n);
          const abs = Math.abs(offset);
          const active = offset === 0;
          const visible = abs <= config.maxVisible;
          const route = routeFor(g);

          return (
            <motion.div
              key={g.label}
              aria-hidden={!visible}
              className="absolute top-0 flex flex-col overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] shadow-lg"
              style={{
                left: "50%",
                marginLeft: -config.cardWidth / 2,
                width: config.cardWidth,
                height: stageHeight,
                pointerEvents: visible ? "auto" : "none",
              }}
              animate={{
                x: offset * config.spacing,
                scale: Math.max(0.4, 1 - abs * config.scaleStep),
                opacity: visible ? Math.max(0, 1 - abs * config.opacityStep) : 0,
                zIndex: 50 - abs * 10,
              }}
              transition={transition}
              onClick={() => {
                if (!active) goTo(index);
              }}
            >
              <div className={`relative w-full shrink-0 ${active ? "" : "cursor-pointer"}`} style={{ height: config.imageHeight }}>
                <Image
                  src={IMAGE_OVERRIDE_BY_LABEL[g.label] ?? g.image}
                  alt={g.label}
                  fill
                  // Only the active card needs to compete for bandwidth on
                  // initial page load — the other 5 stat-card images (all
                  // hero-quality photos) were previously eager too, which
                  // meant every homepage load fetched all 6 at once instead
                  // of just the one actually being shown.
                  loading={active ? "eager" : "lazy"}
                  quality={70}
                  className="rounded-t-xl object-cover"
                  sizes={`(max-width:640px) ${MOBILE.cardWidth}px, (max-width:1024px) ${TABLET.cardWidth}px, ${DESKTOP.cardWidth}px`}
                />
              </div>

              <div
                className={`flex flex-1 flex-col justify-between gap-3 p-5 ${active ? "" : "cursor-pointer"}`}
              >
                <div>
                  {/* Editorial headline: statistic + title as one composition,
                      top-aligned rather than centered. Short values (07/03/
                      05/06) sit beside their title in a grid, number column
                      shrink-to-fit, title taking the rest. Long values
                      (13,032 / 1,046km) stack instead — beside them the
                      title had almost no width left to wrap into. */}
                  {isShortValue(g.value) ? (
                    <div className="flex flex-col gap-1 sm:grid sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start sm:gap-x-5 sm:gap-y-0">
                      <span
                        className={`shrink-0 font-[family-name:var(--font-display)] font-semibold leading-[0.85] text-[var(--accent)] ${valueSizeClass(g.value)}`}
                      >
                        {g.value}
                      </span>
                      <p className="m-0 min-w-0 line-clamp-3 pt-0.5 font-[family-name:var(--font-display)] text-[1.05rem] font-semibold leading-snug text-[var(--foreground)] sm:pt-1">
                        {g.label}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      <span
                        className={`font-[family-name:var(--font-display)] font-semibold leading-[0.85] text-[var(--accent)] ${valueSizeClass(g.value)}`}
                      >
                        {g.value}
                      </span>
                      <p className="m-0 line-clamp-2 font-[family-name:var(--font-display)] text-[1.05rem] font-semibold leading-snug text-[var(--foreground)]">
                        {g.label}
                      </p>
                    </div>
                  )}
                  <p className="m-0 mt-2.5 line-clamp-2 text-[0.85rem] leading-snug text-[color-mix(in_srgb,var(--foreground)_80%,transparent)]">
                    {shortDescription(g.gist)}
                  </p>
                </div>

                {route ? (
                  <Link
                    href={route.href}
                    tabIndex={active ? 0 : -1}
                    aria-hidden={!active}
                    className={`inline-flex min-h-9 w-fit items-center rounded-full bg-[var(--cta)] px-4 text-xs font-semibold text-[#2b2b2b] no-underline transition-colors hover:bg-[var(--cta-bright)] active:brightness-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cta)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface)] ${
                      active ? "" : "pointer-events-none"
                    }`}
                  >
                    {route.linkLabel}
                  </Link>
                ) : null}
              </div>
            </motion.div>
          );
        })}

        {/* Arrows — desktop only (lg+). Absolutely positioned at the
            vertical center of the whole card stage (image + text), which
            is fine once the stage is tall/wide enough that the center
            lands in the image, but on mobile/tablet the narrower, more
            text-heavy card puts that same center point over the statistic
            or title. Below lg, the equivalent controls render in normal
            document flow instead — see the controls row below. */}
        <button
          type="button"
          aria-label="Previous"
          onClick={prev}
          className={`absolute left-2 top-1/2 ${Z_CAROUSEL_ARROW} hidden -translate-y-1/2 items-center justify-center rounded-full border border-[var(--border-subtle)] bg-[color-mix(in_srgb,var(--surface)_75%,transparent)] text-[var(--foreground)] backdrop-blur hover:border-[var(--accent)] hover:text-[var(--accent)] lg:flex lg:h-10 lg:w-10`}
        >
          ←
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={next}
          className={`absolute right-2 top-1/2 ${Z_CAROUSEL_ARROW} hidden -translate-y-1/2 items-center justify-center rounded-full border border-[var(--border-subtle)] bg-[color-mix(in_srgb,var(--surface)_75%,transparent)] text-[var(--foreground)] backdrop-blur hover:border-[var(--accent)] hover:text-[var(--accent)] lg:flex lg:h-10 lg:w-10`}
        >
          →
        </button>
      </div>

      {/* Controls row — mobile/tablet only (below lg). Previous/Next live
          here, in normal flow below the card, instead of overlaid on top
          of it, so they can never cover the statistic or title text. */}
      <div className="mt-4 flex items-center justify-center gap-6 lg:hidden">
        <button
          type="button"
          aria-label="Previous"
          onClick={prev}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border-subtle)] bg-[var(--surface)] text-[var(--foreground)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
        >
          ←
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={next}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border-subtle)] bg-[var(--surface)] text-[var(--foreground)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
        >
          →
        </button>
      </div>

      {/* Dots — mobile/tablet only, same breakpoint as the controls row above. */}
      <div className="mt-3 flex justify-center lg:hidden">
        {items.map((g, index) => (
          <button
            key={g.label}
            type="button"
            aria-label={`Go to ${g.label}`}
            aria-current={index === activeIndex}
            onClick={() => goTo(index)}
            // A 24x24 tap target (WCAG 2.2 target size) around a small visible
            // dot. The buttons sit edge to edge with no gap, so neighbouring
            // targets never overlap; before, the dots were 8x8 with a 4px
            // invisible margin (site audit, 22 Oct 2026).
            className={`flex h-6 items-center justify-center ${index === activeIndex ? "w-8" : "w-6"}`}
          >
            <span
              aria-hidden="true"
              className={`block h-2 rounded-full transition-all ${
                index === activeIndex ? "w-6 bg-[var(--cta)]" : "w-2 bg-[var(--border-subtle)]"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
